import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import {
  EGroupFindPermission,
  EGroupSelfJoinPermission,
  EGroupViewContentPermission,
} from '@/graphql-types/globalTypes';

import {
  useValidFindPermissionOptions_GetGroupPermissionOptions,
  useValidFindPermissionOptions_GetGroupPermissionOptionsVariables,
} from './graphql-types/useValidFindPermissionOptions_GetGroupPermissionOptions';

const GET_GROUP_PERMISSION_OPTIONS = gql`
  query useValidFindPermissionOptions_GetGroupPermissionOptions(
    $input: GroupPermissionOptionsV2Input!
  ) {
    groupPermissionOptionsV2(input: $input) {
      data {
        findPermissionsAndValidRelatedPermissions {
          findPermission
          validSelfJoinPermissions
          validViewContentPermissions
        }
      }
    }
  }
`;

type ViewContentAndJoinPermissions = {
  viewContentPermissions: EGroupViewContentPermission[];
  selfJoinPermissions: EGroupSelfJoinPermission[];
};

export const useValidFindPermissionOptions = (options: {
  parentGroupId?: string;
}): {
  validFindPermissionOptions: Map<
    EGroupFindPermission,
    ViewContentAndJoinPermissions
  >;
  loading: boolean;
} => {
  const { parentGroupId } = options;
  const { data: groupPermissionOptions, loading } = useQuery<
    useValidFindPermissionOptions_GetGroupPermissionOptions,
    useValidFindPermissionOptions_GetGroupPermissionOptionsVariables
  >(GET_GROUP_PERMISSION_OPTIONS, {
    variables: { input: { data: { parentGroupId: parentGroupId || null } } },
  });

  const permissionOptionsData =
    groupPermissionOptions?.groupPermissionOptionsV2.data
      .findPermissionsAndValidRelatedPermissions;

  const permissionOptions = React.useMemo(
    () =>
      permissionOptionsData?.reduce((prevValue, currValue) => {
        return prevValue.set(currValue.findPermission, {
          viewContentPermissions: currValue.validViewContentPermissions,
          selfJoinPermissions: currValue.validSelfJoinPermissions,
        });
      }, new Map<EGroupFindPermission, ViewContentAndJoinPermissions>()) ??
      new Map<EGroupFindPermission, ViewContentAndJoinPermissions>(),
    [permissionOptionsData]
  );

  return {
    validFindPermissionOptions: permissionOptions,
    loading,
  };
};
